import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clapperboard, Camera, MonitorPlay, Zap, Play, Info, HelpCircle } from "lucide-react";
import GameInstructionsModal from "./ui/GameInstructionsModal";

const SCENES = [
  {
    id: "cinematic",
    label: "Cinematic",
    desc: "Widescreen, anamorphic flares, and high contrast for that silver screen feeling.",
    preview: "https://images.unsplash.com/photo-1485846234645-a62644ef7467?auto=format&fit=crop&q=80&w=800",
    style: { filter: "contrast(1.2) saturate(0.8) brightness(0.9)", aspectRatio: "2.35/1" }
  },
  {
    id: "documentary",
    label: "Raw/Docu",
    desc: "Natural lighting, handheld movement, and organic textures.",
    preview: "https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?auto=format&fit=crop&q=80&w=800",
    style: { filter: "sepia(0.1) brightness(1.05)", aspectRatio: "16/9" }
  },
  {
    id: "commercial",
    label: "Vibrant",
    desc: "Crisp, clean, and high-energy colors designed to grab attention immediately.",
    preview: "https://images.unsplash.com/photo-1611540881474-bf7c8731bfd2?auto=format&fit=crop&q=80&w=800",
    style: { filter: "saturate(1.4) contrast(1.1)", aspectRatio: "16/9" }
  }
];

const FilmWorkshop = () => {
  const [activeScene, setActiveScene] = useState(SCENES[0]);
  const [showInstructions, setShowInstructions] = useState(false);

  return (
    <section id="workshop" className="py-32 relative overflow-hidden bg-[var(--ink-2)] border-y border-[var(--rule)]">
      {/* Background Decor */}
      <div className="absolute top-0 right-0 w-1/2 h-full bg-[radial-gradient(circle_at_70%_30%,rgba(201,169,97,0.05)_0%,transparent_70%)] pointer-events-none" />
      
      <div className="container-xl px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          
          {/* Left: Interactive Viewport */}
          <div className="relative">
            <div className="mb-8 flex items-center gap-4">
              <div className="w-12 h-12 rounded-full border border-[var(--gold)] flex items-center justify-center">
                <Clapperboard className="text-[var(--gold)]" size={20} />
              </div>
              <div>
                <h3 className="font-display text-2xl text-[var(--cream)]">Meraki Shot Lab</h3>
                <p className="text-[10px] uppercase tracking-widest text-[var(--muted)]">Interactive Director's Monitor</p>
              </div>
            </div>

            <div className="relative rounded-sm overflow-hidden bg-black border border-white/10 shadow-2xl group">
              {/* Camera Overlay Elements */}
              <div className="absolute top-4 left-4 z-20 flex gap-2">
                <div className="px-2 py-1 bg-red-600 rounded-sm font-impact text-[10px] animate-pulse uppercase">REC</div>
                <div className="px-2 py-1 bg-black/60 rounded-sm font-impact text-[10px] text-white/80">4K • 24FPS</div>
              </div>
              <div className="absolute bottom-4 right-4 z-20">
                <div className="px-2 py-1 bg-black/60 rounded-sm font-impact text-[10px] text-white/80 uppercase">ISO 400 • f/2.8</div>
              </div>
              
              {/* Viewport content */}
              <div className="flex items-center justify-center bg-[#050505] overflow-hidden aspect-[4/3] md:aspect-video">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={activeScene.id}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="w-full h-full relative"
                  >
                    <div 
                      className="w-full h-full bg-cover bg-center transition-all duration-1000"
                      style={{ 
                        backgroundImage: `url(${activeScene.preview})`,
                        ...activeScene.style
                      }}
                    />
                    {/* Cinematic Grain Overlays */}
                    <div className="absolute inset-0 pointer-events-none opacity-40 mix-blend-overlay bg-[url('https://www.transparenttextures.com/patterns/60-lines.png')]" />
                  </motion.div>
                </AnimatePresence>
              </div>

              {/* Viewport Frame */}
              <div className="absolute inset-0 border-[20px] border-black/40 pointer-events-none z-10" />
            </div>

            <p className="mt-6 text-[13px] text-[var(--muted)] italic flex items-center gap-2">
              <Info size={14} /> Tap selectors to preview different cinematic treatments.
            </p>
          </div>

          {/* Right: Selectors */}
          <div className="max-w-md">
            <span className="font-impact text-[var(--gold)] tracking-[0.4em] uppercase text-[11px] mb-4 block">Interactive Workshop</span>
            <h2 className="font-display text-4xl md:text-5xl text-[var(--cream)] mb-8 leading-tight">
              Direct the <span className="italic gold-text">Soul</span> of the Frame
            </h2>
            <button 
              onClick={() => setShowInstructions(true)}
              className="mb-8 flex items-center gap-2 text-[var(--gold)]/60 hover:text-[var(--gold)] transition-colors text-[10px] font-impact tracking-widest uppercase"
            >
              <HelpCircle size={14} />
              How to Use Lab
            </button>
            
            <div className="space-y-6">
              {SCENES.map((scene) => (
                <button
                  key={scene.id}
                  onClick={() => setActiveScene(scene)}
                  className={`w-full text-left p-6 rounded-xl border transition-all duration-500 group ${
                    activeScene.id === scene.id 
                      ? "bg-[rgba(201,169,97,0.1)] border-[var(--gold)]" 
                      : "bg-white/5 border-white/10 hover:border-white/30"
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className={`font-impact text-lg tracking-wider ${activeScene.id === scene.id ? "text-[var(--gold)]" : "text-[var(--cream)]"}`}>
                      {scene.label}
                    </span>
                    {activeScene.id === scene.id && (
                      <Zap size={16} className="text-[var(--gold)] animate-pulse" />
                    )}
                  </div>
                  <p className="text-[var(--muted)] text-xs leading-relaxed group-hover:text-[var(--cream-muted)] transition-colors">
                    {scene.desc}
                  </p>
                </button>
              ))}
            </div>

            <div className="mt-12">
              <div className="p-1 rounded-full bg-[var(--rule)] flex items-center max-w-xs">
                <div className="w-10 h-10 rounded-full bg-[var(--gold)] flex items-center justify-center">
                  <Play size={16} className="text-[var(--ink)] fill-[var(--ink)]" />
                </div>
                <span className="ml-4 font-impact text-[10px] tracking-widest text-[var(--cream)] uppercase">Start Production Session</span>
              </div>
            </div>
          </div>

        </div>
      </div>

      <GameInstructionsModal 
        isOpen={showInstructions}
        onClose={() => setShowInstructions(false)}
        title="Meraki Shot Lab"
        instructions={[
          { title: "Select a Style", desc: "Choose between 'Cinematic', 'Raw/Docu', and 'Vibrant' shot styles from the sidebar to see their visual impact." },
          { title: "Monitor the Viewport", desc: "Watch the interactive director's monitor to see how the aspect ratio, contrast, and color grading shift with each selection." },
          { title: "Understand Graded Assets", desc: "Each style uses a unique set of cinematic grain overlays and color filters to create a specific emotional tone." },
          { title: "Start Production", desc: "Once you've explored the styles, hit 'Start Production Session' to finalize the creative direction for the project." }
        ]}
      />
    </section>
  );
};

export default FilmWorkshop;
